import { useCallback, useEffect, useRef, useState } from 'react';
import { deliveryQualityService } from 'services/deliveryQualityService';

/**
 * Hook para consumir /ai-performance/metrics con auto-refresh configurable.
 *
 * Diseno:
 * - `loading` solo es true en el primer fetch (skeleton). En refrescos
 *   posteriores usamos `refreshing` para mostrar un mini indicador sin
 *   parpadear el dashboard completo.
 * - `AbortController` por request para evitar que un poll lento sobreescriba
 *   uno mas reciente al regresar.
 * - Polling se pausa automaticamente si la pestana esta oculta
 *   (`document.visibilityState === 'hidden'`) y se reanuda al volver
 *   (con un fetch inmediato para refrescar lo que se perdio).
 * - Cuando hay error, NO descartamos `data` previo: el dashboard sigue
 *   mostrando el ultimo snapshot bueno con un banner degradado.
 *
 * @param {object}   options
 * @param {object}   options.params     - { window?, from?, to? } pasado al servicio
 * @param {number}   options.refreshMs  - Intervalo de polling en ms. null/0 = off
 */
export default function useAiMetrics({ params, refreshMs }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const abortRef = useRef(null);
  const intervalRef = useRef(null);
  // Serializamos params para usarlos como dep estable en useEffect.
  const paramsKey = JSON.stringify(params || {});

  const doFetch = useCallback(
    async ({ silent } = {}) => {
      if (abortRef.current) {
        try {
          abortRef.current.abort();
        } catch (e) {
          // ignored
        }
      }
      const controller = new AbortController();
      abortRef.current = controller;

      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const r = await deliveryQualityService.getAiPerformanceMetrics({
          ...(params || {}),
          // axios no lleva el signal por defecto; lo pasamos via config si esta
          // disponible. Como el service helper no expone config, usamos un
          // workaround: cancelamos manualmente al desmontar / re-fetch.
        });
        if (controller.signal.aborted) return;
        setData(r.data);
        setError(null);
        setLastUpdatedAt(Date.now());
      } catch (e) {
        if (controller.signal.aborted) return;
        const detail =
          e?.response?.data?.detail || e?.response?.data?.message || e?.message || 'Error';
        setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [params],
  );

  // Fetch inicial / cuando cambian los params (ventana / from-to).
  useEffect(() => {
    doFetch({ silent: false });
    return () => {
      if (abortRef.current) {
        try {
          abortRef.current.abort();
        } catch (e) {
          // ignored
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  // Auto-refresh: respeta visibilityState.
  useEffect(() => {
    if (!refreshMs || refreshMs <= 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return undefined;
    }

    const tick = () => {
      if (document.visibilityState === 'hidden') return;
      doFetch({ silent: true });
    };

    intervalRef.current = setInterval(tick, refreshMs);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Refresca inmediatamente al volver a la pestana para no mostrar
        // datos viejos durante un ciclo entero.
        doFetch({ silent: true });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshMs, paramsKey]);

  const refresh = useCallback(() => doFetch({ silent: !!data }), [doFetch, data]);

  return {
    data,
    loading,
    refreshing,
    error,
    lastUpdatedAt,
    refresh,
  };
}
